// What a document row's stage badge says (FB-1024 #4, sc-11034), and which stages
// a reviewer can set by hand from the row's stage dropdown.
import type { CaseDocument, DocumentStage, DocumentStatus } from '../../types';

/** The sidebar's view of each document's latest job, which `documentStage` reads. */
export interface JobState {
  processingDocumentIds?: Set<string>;
  failedDocumentIds?: Set<string>;
  rescanDotStatus?: Map<string, unknown>;
  /**
   * Documents whose detection produced results the reviewer can work through
   * (the case response's `progress.has_results`, i.e. the sidebar's entity
   * progress map). An `unprocessed` document outside it has no finished scan.
   */
  analyzedDocumentIds?: { has(documentId: string): boolean };
}

/**
 * The stages a reviewer sets by hand, in menu order. `completed` is not one of
 * them: Finalize sets it (and the viewer's Edit button takes it back to
 * `redacted`), so a completed document's stage is read-only.
 */
export const MANUAL_STAGES: DocumentStatus[] = ['unprocessed', 'needs_redaction', 'redacted'];

/**
 * The stage a review status shows as. `unprocessed` is the status from upload
 * until a reviewer moves the document on, so it reads "Suggestions ready" only
 * once detection has produced results, and "Not analyzed" before that (no job
 * yet, or its only job was cancelled).
 */
export function statusStage(status: DocumentStatus, analyzed: boolean): DocumentStage {
  if (status === 'unprocessed' && !analyzed) return 'not_analyzed';
  return status;
}

function isAnalyzed(doc: CaseDocument, jobs: JobState): boolean {
  if (!doc.is_container) return jobs.analyzedDocumentIds?.has(doc.id) ?? false;
  // A container is never scanned itself: its suggestions are its files'.
  return (doc.children ?? []).some((c) => isAnalyzed(c, jobs));
}

/**
 * The stage a row's badge names: Analyzing while its initial scan runs (a
 * rescan keeps the review status), Failed when its latest job failed, else its
 * review status. A container is Analyzing while any of its files is.
 */
export function documentStage(doc: CaseDocument, jobs: JobState): DocumentStage {
  if (doc.is_container) {
    if ((doc.children ?? []).some((c) => documentStage(c, jobs) === 'analyzing')) return 'analyzing';
    return statusStage(doc.status, isAnalyzed(doc, jobs));
  }
  if (jobs.processingDocumentIds?.has(doc.id) && !jobs.rescanDotStatus?.has(doc.id)) return 'analyzing';
  if (jobs.failedDocumentIds?.has(doc.id)) return 'failed';
  return statusStage(doc.status, isAnalyzed(doc, jobs));
}

/**
 * Whether a row's stage is a dropdown rather than a read-only badge: the same
 * rule the viewer header's stage select had — not while the document is being
 * analyzed, not once it is completed — and never for a container, nor for a
 * document whose latest job failed (retry it first).
 */
export function canChangeStage(doc: CaseDocument, stage: DocumentStage): boolean {
  if (doc.is_container) return false;
  return stage !== 'analyzing' && stage !== 'failed' && stage !== 'completed';
}
